import { Box, type DrawerProps, Grid, IconButton, Stack, Typography } from "@mui/material";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { Drawer, DrawerContent } from "Components/Analyses/_Shared/Drawer";
import { InvestmentCaseContext } from "Components/Analyses/_Shared/InvestmentCaseContext";
import { Button } from "Components/_Shared/Buttons/Button";
import { ReactComponent as CloseIcon } from "Components/_Shared/Icons/Assets/close.svg";
import { DrawType } from "Forms/Schemas/Goals/otherGoalFormSchema";
import { type AnalysisClientItemRelationType, GoalOverviewSubType, GoalOverviewType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useContext, useRef, useState } from "react";
import { ChildrenGoalPane } from "./ChildrenGoalPane";
import { FinancialReserveForm } from "./FinancialReserveForm";
import { GoalTile } from "./GoalTile";
import { HousingMortgageGoalForm } from "./HousingMortgageGoalForm";
import { HousingOtherGoalForm } from "./HousingOtherGoalForm";
import { HousingPropertyGoalForm } from "./HousingPropertyGoalForm";
import { OtherGoalMonthlyForm } from "./OtherGoalMonthlyForm";
import { OtherGoalOneTimeForm } from "./OtherGoalOneTimeForm";
import { PensionGoalForm } from "./PensionGoalForm";
import { VehicleGoalForm } from "./VehicleGoalForm";

type Props = {
  readonly clientId?: string;
  readonly clientRelationType?: AnalysisClientItemRelationType;
  readonly onClose: () => void;
} & DrawerProps;

export type SubmitGoalFormHandle = {
  readonly submitForm: () => void;
};

export const CreateGoalDrawer: React.FC<Props> = ({ clientId, clientRelationType, onClose: propsOnClose, ...rest }) => {
  const { t } = useResource();
  const { readOnly: isAnalysisReadOnly } = useContext(AnalysisDetailContext);
  const { isInvestmentCaseLocked } = useContext(InvestmentCaseContext);
  const readOnly = isAnalysisReadOnly || isInvestmentCaseLocked;
  const [goalType, setGoalType] = useState<GoalOverviewType>(GoalOverviewType.pension);
  const [goalSubType, setGoalSubType] = useState<GoalOverviewSubType>(GoalOverviewSubType.housing_property);
  const [drawType, setDrawType] = useState(DrawType.LumpSum);

  const createVehicleGoalFormRef = useRef<SubmitGoalFormHandle>(null);
  const emergencyFundFormRef = useRef<SubmitGoalFormHandle>(null);
  const childrenGoalFormRef = useRef<SubmitGoalFormHandle>(null);

  const otherGoalFormRef = useRef<SubmitGoalFormHandle>(null);
  const otherGoalMonthlyFormRef = useRef<SubmitGoalFormHandle>(null);

  const housingOtherFormRef = useRef<SubmitGoalFormHandle>(null);
  const housingMortgageFormRef = useRef<SubmitGoalFormHandle>(null);
  const housingPropertyFormRef = useRef<SubmitGoalFormHandle>(null);

  const pensionFormRef = useRef<SubmitGoalFormHandle>(null);

  const onDrawTypeChange = (drawType: DrawType) => {
    setDrawType(drawType);
  };

  const onSubTypeChange = (goalSubType: GoalOverviewSubType) => {
    setGoalSubType(goalSubType);
  };

  const resetToDefault = () => {
    setGoalType(GoalOverviewType.pension);
    setDrawType(DrawType.LumpSum);
    setGoalSubType(GoalOverviewSubType.housing_property);
  };

  const onClose = () => {
    resetToDefault();
    propsOnClose();
  };

  return (
    <Drawer {...rest} onClose={onClose}>
      <Box display="flex" justifyContent="space-between" alignItems="center" padding={Spacing.XXL}>
        <Typography variant="h2">{t(Resources.AnalysisDetail.Goals.NewGoal)}</Typography>

        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DrawerContent>
        <Box>
          <Box px={Spacing.XXL} mb={Spacing.XXL}>
            <Stack spacing={Spacing.XL}>
              <Typography variant="subtitle1" fontWeight={600}>
                {t(Resources.AnalysisDetail.Goals.GoalType.Title)}
              </Typography>

              <Grid container spacing={Spacing.M}>
                {(Object.keys(GoalOverviewType) as Array<GoalOverviewType>).map((value) => {
                  return (
                    <Grid key={value} size={3}>
                      <GoalTile
                        active={value === goalType}
                        goalType={value}
                        onGoalSelect={(type) => {
                          resetToDefault();
                          setGoalType(type);
                        }}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </Stack>
          </Box>

          <Box mb={Spacing.XXL} px={Spacing.XXL}>
            {goalType === GoalOverviewType.pension && <PensionGoalForm onClose={onClose} ref={pensionFormRef} />}
            {goalType === GoalOverviewType.vehicle && <VehicleGoalForm onClose={onClose} ref={createVehicleGoalFormRef} />}
            {goalType === GoalOverviewType.financial_reserve && <FinancialReserveForm onClose={onClose} ref={emergencyFundFormRef} />}
            {goalType === GoalOverviewType.other && drawType === DrawType.LumpSum && (
              <OtherGoalOneTimeForm onClose={onClose} onDrawTypeChange={onDrawTypeChange} ref={otherGoalFormRef} />
            )}
            {goalType === GoalOverviewType.other && drawType === DrawType.Monthly && (
              <OtherGoalMonthlyForm onClose={onClose} onDrawTypeChange={onDrawTypeChange} ref={otherGoalMonthlyFormRef} />
            )}
            {goalType === GoalOverviewType.housing && goalSubType === GoalOverviewSubType.housing_other && (
              <HousingOtherGoalForm onSubTypeChange={onSubTypeChange} onClose={onClose} ref={housingOtherFormRef} />
            )}
            {goalType === GoalOverviewType.housing && goalSubType === GoalOverviewSubType.housing_mortgage && (
              <HousingMortgageGoalForm onSubTypeChange={onSubTypeChange} onClose={onClose} ref={housingMortgageFormRef} />
            )}
            {goalType === GoalOverviewType.housing && goalSubType === GoalOverviewSubType.housing_property && (
              <HousingPropertyGoalForm onSubTypeChange={onSubTypeChange} onClose={onClose} ref={housingPropertyFormRef} />
            )}
            {goalType === GoalOverviewType.children && <ChildrenGoalPane onClose={onClose} ref={childrenGoalFormRef} />}
          </Box>
        </Box>
      </DrawerContent>

      <Box display="flex" flexDirection="row-reverse" py={Spacing.XXL} px={Spacing.XXL}>
        <Button
          type="submit"
          disabled={readOnly}
          onClick={() => {
            if (readOnly) {
              return;
            }
            if (goalType === GoalOverviewType.vehicle) {
              createVehicleGoalFormRef.current?.submitForm();
            }
            if (goalType === GoalOverviewType.financial_reserve) {
              emergencyFundFormRef.current?.submitForm();
            }
            if (goalType === GoalOverviewType.other && drawType === DrawType.LumpSum) {
              otherGoalFormRef.current?.submitForm();
            }
            if (goalType === GoalOverviewType.other && drawType === DrawType.Monthly) {
              otherGoalMonthlyFormRef.current?.submitForm();
            }
            if (goalType === GoalOverviewType.housing && goalSubType === GoalOverviewSubType.housing_other) {
              housingOtherFormRef.current?.submitForm();
            }
            if (goalType === GoalOverviewType.housing && goalSubType === GoalOverviewSubType.housing_mortgage) {
              housingMortgageFormRef.current?.submitForm();
            }
            if (goalType === GoalOverviewType.housing && goalSubType === GoalOverviewSubType.housing_property) {
              housingPropertyFormRef.current?.submitForm();
            }
            if (goalType === GoalOverviewType.pension) {
              pensionFormRef.current?.submitForm();
            }
            if (goalType === GoalOverviewType.children) {
              childrenGoalFormRef.current?.submitForm();
            }
          }}
        >
          {t(Resources.Common.Create)}
        </Button>

        <Button
          onClick={onClose}
          color="secondary"
          sx={{
            marginRight: Spacing.XL,
          }}
        >
          {t(Resources.Common.Close)}
        </Button>
      </Box>
    </Drawer>
  );
};
